/**
 * Spatial lookup for solid scenery.
 *
 * Props live off the grid — a bathtub is not a cell — so anything that needs to
 * not walk through one needs a way to ask "what is near me". Both the player's
 * collision and the entities' steering go through this, so a prop that blocks
 * one blocks the other.
 *
 * The index is a uniform hash over the same cell size the level uses. Props are
 * small relative to a cell and there are only a few hundred of them, so a
 * bucket is nearly always 0-3 entries and the query never allocates.
 */

import { TUNING } from './config';
import type { PropSpot, Vec2 } from './types';

export interface PropIndex {
  /**
   * Solid props whose footprint could overlap a circle at (x, z). Fills and
   * returns `out`, which is cleared first. Broad phase only — the caller does
   * the exact test, usually via `resolveCircle`.
   */
  query(x: number, z: number, radius: number, out: PropSpot[]): PropSpot[];

  /** True when a circle of `radius` at (x, z) overlaps any solid prop. */
  blocked(x: number, z: number, radius: number): boolean;

  /**
   * Push a circle out of every prop it overlaps, in place. Returns true when it
   * moved anything. `minHeight` lets a caller ignore props it can step over.
   */
  resolveCircle(pos: Vec2, radius: number, minHeight?: number): boolean;

  readonly count: number;
}

/** Largest footprint half-diagonal in the level, used to size the query halo. */
function halfDiagonal(p: PropSpot): number {
  const f = p.footprint!;
  return Math.hypot(f.halfX, f.halfZ);
}

class Index implements PropIndex {
  private readonly cell: number;
  private readonly buckets = new Map<number, PropSpot[]>();
  /** Query halo: the biggest prop's reach, so a broad-phase hit is never missed. */
  private readonly maxReach: number;
  readonly count: number;

  constructor(props: readonly PropSpot[], cellSize: number) {
    this.cell = Math.max(1, cellSize);
    let solid = 0;
    let reach = 0;

    for (const p of props) {
      if (!p.footprint) continue;
      solid++;
      const r = halfDiagonal(p) * (p.scale || 1);
      if (r > reach) reach = r;
      // Register in every bucket the prop's bounding circle touches, so a prop
      // straddling a boundary is found from either side.
      const minX = Math.floor((p.pos.x - r) / this.cell);
      const maxX = Math.floor((p.pos.x + r) / this.cell);
      const minZ = Math.floor((p.pos.z - r) / this.cell);
      const maxZ = Math.floor((p.pos.z + r) / this.cell);
      for (let cz = minZ; cz <= maxZ; cz++) {
        for (let cx = minX; cx <= maxX; cx++) {
          const key = cx * 73856093 ^ cz * 19349663;
          let bucket = this.buckets.get(key);
          if (!bucket) this.buckets.set(key, (bucket = []));
          bucket.push(p);
        }
      }
    }
    this.count = solid;
    this.maxReach = reach;
  }

  query(x: number, z: number, radius: number, out: PropSpot[]): PropSpot[] {
    out.length = 0;
    if (this.count === 0) return out;
    const halo = radius + this.maxReach;
    const minX = Math.floor((x - halo) / this.cell);
    const maxX = Math.floor((x + halo) / this.cell);
    const minZ = Math.floor((z - halo) / this.cell);
    const maxZ = Math.floor((z + halo) / this.cell);
    for (let cz = minZ; cz <= maxZ; cz++) {
      for (let cx = minX; cx <= maxX; cx++) {
        const bucket = this.buckets.get(cx * 73856093 ^ cz * 19349663);
        if (!bucket) continue;
        // A prop registered in several buckets can be visited twice; the exact
        // tests below are idempotent, so dedup would cost more than it saves.
        for (const p of bucket) if (!out.includes(p)) out.push(p);
      }
    }
    return out;
  }

  blocked(x: number, z: number, radius: number): boolean {
    if (this.count === 0) return false;
    this.query(x, z, radius, this.scratch);
    for (const p of this.scratch) {
      if (p.footprint!.height < TUNING.player.stepOverHeight) continue;
      if (penetration(p, x, z, radius, null)) return true;
    }
    return false;
  }

  resolveCircle(pos: Vec2, radius: number, minHeight = TUNING.player.stepOverHeight): boolean {
    if (this.count === 0) return false;
    let moved = false;
    // Two passes: pushing out of one prop can push into its neighbour, and a
    // corner between two props needs both to be satisfied at once. The broad
    // phase is re-run each pass, because the push moves the circle and the prop
    // it has been shoved into need not have been in range before.
    for (let pass = 0; pass < 2; pass++) {
      this.query(pos.x, pos.z, radius, this.scratch);
      let any = false;
      for (const p of this.scratch) {
        if (p.footprint!.height < minHeight) continue;
        if (penetration(p, pos.x, pos.z, radius, pos)) {
          any = true;
          moved = true;
        }
      }
      if (!any) break;
    }
    return moved;
  }

  /** Reused by every query so the hot path allocates nothing. */
  private readonly scratch: PropSpot[] = [];
}

/**
 * Gap left between a circle and a prop's surface after a push-out.
 *
 * It lives here rather than in each caller because an *exactly* touching circle
 * still reads as overlapping — the overlap test is `d <= radius` — so a push
 * that landed the circle precisely on the surface would report as unresolved
 * for ever. Both the player's collision and the entities' ejection used to add
 * their own margin on top; one skin, at the only place that knows it is needed.
 */
const SKIN = 2e-3;

/**
 * Circle vs rotated box. When `push` is given, the circle is moved out along
 * the shallowest axis, clear of the surface by `SKIN`, and the function returns
 * whether it overlapped at all.
 */
function penetration(p: PropSpot, x: number, z: number, radius: number, push: Vec2 | null): boolean {
  const f = p.footprint!;
  const s = p.scale || 1;
  const hx = f.halfX * s;
  const hz = f.halfZ * s;

  // Into the prop's local frame. yaw = 0 faces -Z, so the prop's local +X is
  // (cos yaw, sin yaw) and its local +Z is (-sin yaw, cos yaw).
  const cos = Math.cos(p.yaw);
  const sin = Math.sin(p.yaw);
  const dx = x - p.pos.x;
  const dz = z - p.pos.z;
  const lx = dx * cos + dz * sin;
  const lz = -dx * sin + dz * cos;

  // Nearest point on the box to the circle centre, in local space.
  const nx = lx < -hx ? -hx : lx > hx ? hx : lx;
  const nz = lz < -hz ? -hz : lz > hz ? hz : lz;
  const ox = lx - nx;
  const oz = lz - nz;
  const d2 = ox * ox + oz * oz;

  if (d2 > radius * radius) return false;
  if (!push) return true;

  let outX: number;
  let outZ: number;
  if (d2 > 1e-8) {
    // Outside the box, overlapping the rounded edge: push along the normal.
    const d = Math.sqrt(d2);
    outX = (ox / d) * (radius + SKIN - d);
    outZ = (oz / d) * (radius + SKIN - d);
  } else {
    // Centre is inside the box. Leave by the nearest face, or the circle would
    // be shoved clean through to the far side.
    const toX = hx - Math.abs(lx);
    const toZ = hz - Math.abs(lz);
    if (toX < toZ) {
      outX = (lx >= 0 ? 1 : -1) * (toX + radius + SKIN);
      outZ = 0;
    } else {
      outX = 0;
      outZ = (lz >= 0 ? 1 : -1) * (toZ + radius + SKIN);
    }
  }

  // Back to world space.
  push.x += outX * cos - outZ * sin;
  push.z += outX * sin + outZ * cos;
  return true;
}

export function makePropIndex(props: readonly PropSpot[], cellSize: number): PropIndex {
  return new Index(props, cellSize);
}

/** An index with nothing in it, for levels built before props existed. */
export const EMPTY_PROP_INDEX: PropIndex = makePropIndex([], 1);
